import { Injectable } from '@angular/core';
import { ApiService } from '@services/api.service';
import { Observable } from 'rxjs';
import { environment } from '@env/environment';
import {
  MarineInsuranceLimitsModel,
  MarineInsuranceManagementModel,
  MarineInsuranceModel,
  MarineInsuranceSearchShipmentModel,
} from '@data-layer/models/marine-insurance';
import { DeepPartial, PaginatedResultModel, ShortModel } from '@data-layer/models/common';
import { enumMarineInsuranceCommodity } from '@data-layer/enums/marine-insurance';
import { MarineInsuranceCountByStateModel } from '@data-layer/models/management/marine-insurance';
import { enumMarineInsuranceStatusType } from '@app/data-layer/enums/marine-insurance/enumInsuranceStatusType.enum';

@Injectable({
  providedIn: 'root',
})
export class MarineInsuranceRepository {
  constructor(private apiService: ApiService) {}

  public getShipment(referenceNumber: string, isShowLoader?: boolean): Observable<MarineInsuranceModel> {
    return this.apiService.get<MarineInsuranceModel>(`${environment.url.prefixInsurance}shipments/${referenceNumber}`, null, {
      isShowLoader,
    });
  }

  public getShipments(params: MarineInsuranceSearchShipmentModel, isShowLoader?: boolean): Observable<MarineInsuranceModel> {
    return this.apiService.get<MarineInsuranceModel>(`${environment.url.prefixInsurance}shipments`, { params }, { isShowLoader });
  }

  public updateShipment(shipment: MarineInsuranceModel, isShowLoader?: boolean): Observable<MarineInsuranceModel> {
    return this.apiService.put<MarineInsuranceModel>(`${environment.url.prefixInsurance}shipments/${shipment.id}`, shipment, null, {
      isShowLoader,
    });
  }

  public deleteShipment(id: number, isShowLoader?: boolean): Observable<never> {
    return this.apiService.delete<never>(`${environment.url.prefixInsurance}shipments/${id}`, null, { isShowLoader });
  }

  public getCommodities(isShowLoader?: boolean): Observable<ShortModel[]> {
    return this.apiService.get<ShortModel[]>(`${environment.url.prefixInsurance}commodities`, null, { isShowLoader });
  }

  public createIsfInsurance(
    commodityId: enumMarineInsuranceCommodity,
    isfId: number,
    isShowLoader?: boolean,
  ): Observable<MarineInsuranceModel> {
    return this.apiService.post<MarineInsuranceModel>(
      `${environment.url.prefixInsurance}v2/isfs/${isfId}/marine-insurance`,
      { commodityId },
      null,
      {
        isShowLoader,
      },
    );
  }

  public createEsInsurance(
    commodityId: enumMarineInsuranceCommodity,
    esId: number,
    isShowLoader?: boolean,
  ): Observable<MarineInsuranceModel> {
    return this.apiService.post<MarineInsuranceModel>(
      `${environment.url.prefixInsurance}v2/entry-summaries/${esId}/marine-insurance`,
      { commodityId },
      null,
      {
        isShowLoader,
      },
    );
  }

  public createLCLQuoteInsurance(
    commodityId: enumMarineInsuranceCommodity,
    quoteId: number,
    isShowLoader?: boolean,
  ): Observable<MarineInsuranceModel> {
    return this.apiService.post<MarineInsuranceModel>(
      `${environment.url.prefixInsurance}freight-quotes/${quoteId}/marine-insurance`,
      { commodityId },
      null,
      {
        isShowLoader,
      },
    );
  }

  public createLCLShipmentInsurance(
    commodityId: enumMarineInsuranceCommodity,
    shipmentId: number,
    isShowLoader?: boolean,
  ): Observable<MarineInsuranceModel> {
    return this.apiService.post<MarineInsuranceModel>(
      `${environment.url.prefixInsurance}freight-shipments/${shipmentId}/marine-insurance`,
      { commodityId },
      null,
      {
        isShowLoader,
      },
    );
  }

  public updateShipmentInsuranceStatus(shipmentId: number, status: enumMarineInsuranceStatusType): Observable<never> {
    return this.apiService.post(`${environment.url.prefixInsurance}freight-shipments/${shipmentId}/marine-insurance/status`, status);
  }

  public createCertificate(id: number, isShowLoader?: boolean): Observable<MarineInsuranceModel> {
    return this.apiService.post<MarineInsuranceModel>(`${environment.url.prefixInsurance}shipments/${id}/certificate`, null, null, {
      isShowLoader,
    });
  }

  public getManagementShipment(id: number, isShowLoader?: boolean): Observable<MarineInsuranceManagementModel> {
    return this.apiService.get<MarineInsuranceManagementModel>(`${environment.url.prefixInsurance}management/shipments/${id}`, null, {
      isShowLoader,
    });
  }

  public getManagementShipments(
    params: MarineInsuranceSearchShipmentModel,
    isShowLoader?: boolean,
  ): Observable<PaginatedResultModel<MarineInsuranceManagementModel>> {
    return this.apiService.get<PaginatedResultModel<MarineInsuranceManagementModel>>(
      `${environment.url.prefixInsurance}management/shipments`,
      { params },
      { isShowLoader },
    );
  }

  public getManagementCountStats(isShowLoader?: boolean): Observable<MarineInsuranceCountByStateModel[]> {
    return this.apiService.get<MarineInsuranceCountByStateModel[]>(
      `${environment.url.prefixInsurance}management/shipments/count-by-status`,
      null,
      { isShowLoader },
    );
  }

  public setManagementFlags(id: number, isShowLoader?: boolean): Observable<MarineInsuranceManagementModel> {
    return this.apiService.put<MarineInsuranceManagementModel>(
      `${environment.url.prefixInsurance}management/shipments/${id}/flags`,
      null,
      null,
      {
        isShowLoader,
      },
    );
  }

  public setManagementRefund(id: number, isShowLoader?: boolean): Observable<MarineInsuranceManagementModel> {
    return this.apiService.put<MarineInsuranceManagementModel>(
      `${environment.url.prefixInsurance}management/shipments/${id}/refund`,
      null,
      null,
      {
        isShowLoader,
      },
    );
  }

  public updateManagementShipment(
    shipment: DeepPartial<MarineInsuranceManagementModel>,
    isShowLoader?: boolean,
  ): Observable<MarineInsuranceManagementModel> {
    return this.apiService.put<MarineInsuranceManagementModel>(
      `${environment.url.prefixInsurance}management/shipments/${shipment.id}`,
      shipment,
      null,
      {
        isShowLoader,
      },
    );
  }

  public confirmDraftPayment(shipmentId: number, isShowLoader?: boolean): Observable<MarineInsuranceManagementModel> {
    return this.apiService.put<MarineInsuranceManagementModel>(
      `${environment.url.prefixInsurance}management/shipments/${shipmentId}/confirm-draft-payment`,
      null,
      null,
      {
        isShowLoader,
      },
    );
  }

  public getManagementLimits(isShowLoader?: boolean): Observable<MarineInsuranceLimitsModel> {
    return this.apiService.get<MarineInsuranceLimitsModel>(`${environment.url.prefixInsurance}management/limits`, null, {
      isShowLoader,
    });
  }
}
